import React, { useEffect, useRef, useState } from 'react';

const template = (blank = false) => `id: new-demo-events
capability_id: urn:pantheon:datahub:event:new-demo
version: 1.0.0
name: new-demo
namespace: datahub.demo
owner: demo-team
description: ${blank ? 'Describe these events.' : 'Observations produced by this demo.'}
event_schema:
  type: object
${blank ? '  properties: {}' : '  required: [value]\n  properties:\n    value:\n      type: number'}
example:
${blank ? '  {}' : '  value: 42'}
access:
  discover_scopes: [datahub.demo.events.read]
  publish_scopes: [datahub.demo.events.publish]
  subscribe_scopes: [datahub.demo.events.read]
`;
const key = entry => `${entry.id}@${entry.version}`;

export default function FabricContractEditor({ contracts = [], value, onChange, onCatalogChange, api, getIdToken, disabled, available, onBusyChange }) {
  const importInput = useRef(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!available) setExpanded(false);
  }, [available]);
  const [editor, setEditor] = useState(null);
  const [yaml, setYaml] = useState('');
  const [validation, setValidation] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = contracts.find(entry => entry.id === value?.id && entry.version === value?.version);
  const published = contracts.filter(entry => entry.status === 'published');
  const drafts = contracts.filter(entry => entry.status === 'draft');
  const locked = disabled || busy || !available;
  const request = async (url, method, body) => {
    const token = await getIdToken();
    if (!token) throw new Error('Sign in again to manage contracts.');
    const response = await fetch(url, { method, headers: { Authorization: token, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || result.message || `Contract request failed (${response.status}).`);
    return result;
  };
  const run = async action => {
    if (locked) return;
    setBusy(true); onBusyChange(true); setError(''); setMessage('');
    try { await action(); }
    catch (failure) { setError(failure.message || 'Could not save the contract.'); }
    finally { setBusy(false); onBusyChange(false); }
  };
  const open = entry => {
    setEditor(entry || { status: 'new' });
    setYaml(entry?.yaml || template());
    setValidation(entry?.descriptor ? entry : null);
    setError(''); setMessage('');
  };
  const saveDraft = async () => {
    const parsed = await request(api.validateContract, 'POST', { yaml });
    setValidation(parsed);
    const existing = editor?.status === 'draft' ? editor : null;
    const updated = await request(existing ? api.contractVersion(existing.id, existing.version) : api.contracts,
      existing ? 'PATCH' : 'POST', { yaml });
    onCatalogChange(updated);
    const entry = updated.contracts.find(item => item.id === parsed.id && item.version === parsed.version);
    setEditor(entry);
    return entry;
  };
  const publish = () => run(async () => {
    const entry = await saveDraft();
    const updated = await request(api.publishContract(entry.id, entry.version), 'POST');
    onCatalogChange(updated);
    const saved = updated.contracts.find(item => item.id === entry.id && item.version === entry.version);
    setEditor(saved); setValidation(saved);
    onChange({ id: saved.id, version: saved.version });
    setMessage('Published and selected. Save the preset to attach this version.');
  });
  const newVersion = entry => {
    const next = entry.version.split('-')[0].split('.').map(Number);
    next[2] += 1;
    open(null);
    setYaml(entry.yaml.replace(/^version:.*$/m, `version: ${next.join('.')}`));
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([yaml], { type: 'application/yaml' }));
    const link = document.createElement('a'); link.href = url;
    link.download = `${validation?.id || 'event-contract'}.${validation?.version || 'draft'}.yaml`;
    link.click(); URL.revokeObjectURL(url);
  };

  return <section className="fabric-contract-editor" aria-label="Fabric event contract">
    <p id="fabric-tools-requirements" className="module-catalog-note">Fabric tools require both DataHub Backend and Data Fabric Agent in this preset.</p>
    <button type="button" className="fabric-tools-toggle" aria-expanded={Boolean(expanded && available)} aria-controls="fabric-tools-panel" aria-describedby="fabric-tools-requirements" disabled={locked} onClick={() => setExpanded(current => !current)}>
      <span aria-hidden="true">{expanded && available ? '▾' : '▸'}</span> Fabric tools
    </button>
    {expanded && available && <div id="fabric-tools-panel" className="fabric-tools-panel">
    <p className="module-catalog-note">Pin a published YAML contract for new nodes. Deployed nodes retain their original version.</p>
    <label className="fabric-contract-field">Published version<select value={value ? key(value) : ''} disabled={locked || !available} onChange={event => {
      const entry = published.find(item => key(item) === event.target.value);
      onChange(entry ? { id: entry.id, version: entry.version } : null);
    }}>
      <option value="">No contract selected</option>
      {published.map(entry => <option key={key(entry)} value={key(entry)}>{entry.descriptor.name} — {entry.id} / {entry.version}</option>)}
    </select></label>
    {selected && <div className="fabric-contract-summary">
      <strong>{selected.descriptor.capability_id}</strong>
      <p>{selected.descriptor.description}</p>
      <div className="fabric-contract-actions">
        <button type="button" disabled={locked} onClick={() => open(selected)}>View YAML</button>
        <button type="button" disabled={locked} onClick={() => newVersion(selected)}>New version</button>
      </div>
    </div>}
    <div className="fabric-contract-actions">
      <button type="button" disabled={locked} onClick={() => open(null)}>New contract</button>
      <button type="button" disabled={locked} onClick={() => { open(null); setYaml(template(true)); }}>Blank YAML</button>
      <button type="button" disabled={locked} onClick={() => importInput.current?.click()}>Import YAML</button>
      <input ref={importInput} type="file" hidden aria-label="Import contract YAML" accept=".yaml,.yml,application/yaml,text/yaml" disabled={locked} onChange={event => {
        const file = event.target.files?.[0]; event.target.value = '';
        if (!file) return;
        if (file.size > 32768) { setError('Provide a YAML document of at most 32 KiB.'); return; }
        run(async () => { const contents = await file.text(); open(null); setYaml(contents); });
      }} />
    </div>
    {drafts.length > 0 && <label className="fabric-contract-field">Saved drafts<select value="" disabled={locked} onChange={event => {
      const entry = drafts.find(item => key(item) === event.target.value); if (entry) open(entry);
    }}><option value="">Choose a draft to edit</option>{drafts.map(entry => <option key={key(entry)} value={key(entry)}>{entry.id} / {entry.version}</option>)}</select></label>}
    {editor && <div className="fabric-contract-yaml-panel">
      <label className="fabric-contract-field" htmlFor="fabric-contract-yaml">{editor.status === 'published' ? 'YAML (published, read only)' : 'YAML'}</label>
      <p className="module-catalog-note">Include the message schema and an example. Publishing uses the existing DataHub demo scopes.</p>
      <textarea id="fabric-contract-yaml" aria-label="Contract YAML" className="fabric-contract-yaml" rows={20} value={yaml} readOnly={editor.status === 'published'}
        disabled={locked} spellCheck={false} onChange={event => { setYaml(event.target.value); setValidation(null); setMessage(''); }} />
      <div className="fabric-contract-actions">
        <button type="button" disabled={locked} onClick={() => run(async () => {
          setValidation(await request(api.validateContract, 'POST', { yaml })); setMessage('YAML, schema and example are valid.');
        })}>Validate and preview</button>
        {editor.status !== 'published' && <>
          <button type="button" disabled={locked} onClick={() => run(async () => { await saveDraft(); setMessage('Draft saved. Publish it before attaching it to a preset.'); })}>Save draft</button>
          <button type="button" disabled={locked || !available} onClick={publish} className="fabric-contract-primary">Publish and select</button>
        </>}
        <button type="button" disabled={locked} onClick={download}>Download YAML</button>
        {editor.status === 'draft' && <button type="button" disabled={locked} onClick={() => run(async () => {
          onCatalogChange(await request(api.contractVersion(editor.id, editor.version), 'DELETE')); setEditor(null); setValidation(null); setMessage('Draft deleted.');
        })}>Delete draft</button>}
        <button type="button" disabled={locked} onClick={() => { setEditor(null); setValidation(null); setError(''); }}>Close editor</button>
      </div>
      {validation && <div className="fabric-contract-preview">
        <strong>{validation.descriptor.capability_id} / {validation.version}</strong>
        <span>Example message{validation.example === undefined ? ' (add one before publishing)' : ''}</span>
        <pre>{JSON.stringify(validation.example ?? {}, null, 2)}</pre>
      </div>}
    </div>}
    {message && <p role="status">{message}</p>}
    {error && <div className="node-modal-error" role="alert">{error}</div>}
    </div>}
  </section>;
}
